import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createGame, flipCard, checkMatch } from "./engine.js";
import type { MemoryGame } from "./types.js";
import ConfettiSplash from "../../lib/ConfettiSplash.js";

type Mode = "single" | "local" | "multiplayer";
type Level = "easy" | "medium" | "hard";
type PlayerColor = "red" | "yellow" | "green" | "purple" | "orange" | "teal";
type MpView = "lobby" | "join-form";

const COLOR_OPTIONS: PlayerColor[] = ["red", "yellow", "green", "purple", "orange", "teal"];
const LEVEL_PAIRS: Record<Level, number> = { easy: 4, medium: 8, hard: 12 };

function toLabel(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export default function MemoryMatchPage({ hideTitle = false }: { hideTitle?: boolean }) {
  const [mode, setMode] = useState<Mode>("single");
  const [level, setLevel] = useState<Level>("medium");
  const [game, setGame] = useState<MemoryGame>(() => createGame(LEVEL_PAIRS.medium));
  const [gameId, setGameId] = useState(0);
  const [message, setMessage] = useState("");

  // local 2P state
  const [lP1Name, setLP1Name] = useState("Player 1");
  const [lP2Name, setLP2Name] = useState("Player 2");
  const [localScores, setLocalScores] = useState<[number, number]>([0, 0]);
  const [localTurn, setLocalTurn] = useState<1 | 2>(1);

  // multiplayer state
  const [mpView, setMpView] = useState<MpView>("lobby");
  const [mpConnected, setMpConnected] = useState(false);
  const [mpPlayerId, setMpPlayerId] = useState<1 | 2 | null>(null);
  const [roomCode, setRoomCode] = useState("");
  const [mpToken, setMpToken] = useState("");
  const [p1Name, setP1Name] = useState("Player 1");
  const [p1Color, setP1Color] = useState<PlayerColor>("red");
  const [opponentName, setOpponentName] = useState("");
  const [yourTurn, setYourTurn] = useState(false);
  const [mpError, setMpError] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [joinName, setJoinName] = useState("Player 2");
  const [joinColor, setJoinColor] = useState<PlayerColor>("yellow");
  const [mpScores, setMpScores] = useState<[number, number]>([0, 0]);

  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingRef = useRef(false);
  const modeRef = useRef(mode);
  const localTurnRef = useRef(localTurn);
  modeRef.current = mode;
  localTurnRef.current = localTurn;
  const roomCodeRef = useRef(roomCode);
  const mpPlayerIdRef = useRef(mpPlayerId);
  const mpConnectedRef = useRef(mpConnected);

  roomCodeRef.current = roomCode;
  mpPlayerIdRef.current = mpPlayerId;
  mpConnectedRef.current = mpConnected;

  const cols = mode === "multiplayer" ? 4 : level === "hard" ? 6 : 4;
  const gridWidth = cols === 6 ? 570 : 380;

  const p1 = lP1Name.trim() || "Player 1";
  const p2 = lP2Name.trim() || "Player 2";

  const status = useMemo(() => {
    if (mode === "local") {
      if (game.isComplete) {
        if (localScores[0] > localScores[1]) return `${p1} wins! ${localScores[0]} – ${localScores[1]}`;
        if (localScores[1] > localScores[0]) return `${p2} wins! ${localScores[1]} – ${localScores[0]}`;
        return `Draw! ${localScores[0]} – ${localScores[1]}`;
      }
      if (game.waiting) return "Checking...";
      return `${localTurn === 1 ? p1 : p2}'s turn  ·  Score: ${localScores[0]} – ${localScores[1]}`;
    }
    if (mode === "multiplayer") {
      const myScore = mpPlayerId ? mpScores[mpPlayerId - 1] : 0;
      const oppScore = mpPlayerId ? mpScores[mpPlayerId === 1 ? 1 : 0] : 0;
      if (game.isComplete) {
        if (myScore > oppScore) return `You win! ${myScore} – ${oppScore}`;
        if (oppScore > myScore) return `${opponentName || "Opponent"} wins! ${oppScore} – ${myScore}`;
        return `Draw! ${myScore} – ${oppScore}`;
      }
      if (game.waiting) return "Checking...";
      return yourTurn ? "Your turn" : `${opponentName || "Opponent"}'s turn  ·  Score: ${myScore} – ${oppScore}`;
    }
    if (game.isComplete) return `You matched all ${game.totalPairs} pairs in ${game.moves} moves!`;
    if (game.waiting) return "Checking...";
    return `Pairs found: ${game.matchedPairs} / ${game.totalPairs}  ·  Moves: ${game.moves}`;
  }, [game, mode, mpPlayerId, yourTurn, opponentName, mpScores, localScores, localTurn, p1, p2]);

  // ---- helpers ------------------------------------------------------------

  async function apiPost(path: string, body: object) {
    try {
      const res = await fetch(path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      return await res.json();
    } catch {
      return { type: "error", message: "Network error." };
    }
  }

  function newGame(pairs: number) {
    setGame(createGame(pairs));
    setGameId((n) => n + 1);
    setMessage("");
    setLocalScores([0, 0]);
    setLocalTurn(1);
  }

  // ---- server event handling ----------------------------------------------

  function handleServerEvent(event: any) {
    switch (event.type) {
      case "opponent-joined":
        setOpponentName(event.playerName);
        setMpConnected(true);
        setMessage(`Opponent ${event.playerName} joined! Press New Game to start.`);
        break;
      case "game-state":
        setGame(event.game);
        setYourTurn(event.yourTurn);
        if (event.game.scores) setMpScores(event.game.scores);
        break;
      case "opponent-left":
        setMpConnected(false);
        setMpView("lobby");
        setRoomCode("");
        setOpponentName("");
        setMpPlayerId(null);
        setMpToken("");
        setMpScores([0, 0]);
        setMessage("Opponent left the game.");
        newGame(LEVEL_PAIRS[level]);
        break;
      case "error":
        setMpError(event.message);
        break;
    }
  }

  // ---- polling ------------------------------------------------------------

  const startPolling = useCallback(() => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    async function poll() {
      if (!pollingRef.current) return;
      const code = roomCodeRef.current;
      const pid = mpPlayerIdRef.current;
      if (!code || !pid) { pollingRef.current = false; return; }
      try {
        const res = await fetch(`/api/poll?code=${encodeURIComponent(code)}&playerId=${pid}`);
        const data = await res.json();
        for (const event of data.events) {
          handleServerEvent(event);
          if (event.type === "opponent-left") { pollingRef.current = false; return; }
        }
      } catch { /* retry */ }
      if (pollingRef.current) pollRef.current = setTimeout(poll, 200);
    }
    poll();
  }, []);

  function stopPolling() {
    pollingRef.current = false;
    if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
  }

  useEffect(() => { return () => stopPolling(); }, []);

  // ---- game actions -------------------------------------------------------

  function handleFlip(index: number) {
    if (modeRef.current === "multiplayer") {
      if (!yourTurn) return;
      apiPost("/api/mm/move", { code: roomCode, playerId: mpPlayerId, token: mpToken, index });
      return;
    }
    setGame((prev) => {
      const result = flipCard(prev, index);
      if (!result.ok) { setMessage(result.reason); return prev; }
      setMessage("");
      return result.game;
    });
  }

  // After two cards flipped + delay → resolve match
  useEffect(() => {
    if (!game.waiting || modeRef.current === "multiplayer") return;
    const timer = window.setTimeout(() => {
      const resolved = checkMatch(game);
      if (modeRef.current === "local") {
        const [a, b] = game.flipped;
        const wasMatch = resolved.cards[a].state === "matched";
        if (wasMatch) {
          setLocalScores((s) => {
            const next: [number, number] = [s[0], s[1]];
            next[localTurnRef.current - 1] += 1;
            return next;
          });
        } else {
          setLocalTurn((t) => (t === 1 ? 2 : 1));
        }
      }
      setGame(resolved);
    }, 800);
    return () => window.clearTimeout(timer);
  }, [game.waiting, game]);

  function resetGame() {
    if (mode === "multiplayer") {
      apiPost("/api/mm/reset", { code: roomCode, playerId: mpPlayerId, token: mpToken });
      return;
    }
    newGame(LEVEL_PAIRS[level]);
  }

  function handleModeChange(next: Mode) {
    if (mode === "multiplayer") {
      if (roomCode && mpToken) apiPost("/api/leave", { code: roomCode, playerId: mpPlayerId, token: mpToken });
      stopPolling();
    }
    setMode(next);
    setLocalScores([0, 0]);
    setLocalTurn(1);
    newGame(LEVEL_PAIRS[level]);
    if (next !== "multiplayer") setMessage("");
  }

  // ---- multiplayer room actions -------------------------------------------

  async function handleCreateRoom() {
    const result = await apiPost("/api/create-room", {
      playerName: p1Name.trim() || "Player 1",
      playerColor: p1Color,
      gameType: "memory-match",
    });
    if (result.type === "error") { setMpError(result.message); return; }
    roomCodeRef.current = result.code;
    mpPlayerIdRef.current = result.playerId;
    setRoomCode(result.code);
    setMpPlayerId(result.playerId);
    setMpToken(result.token);
    setMpView("lobby");
    setMessage(`Room created! Share this code: ${result.code}`);
    startPolling();
  }

  async function handleJoinRoom() {
    if (!joinCode.trim()) { setMpError("Enter a room code."); return; }
    const result = await apiPost("/api/join-room", {
      code: joinCode.trim(),
      playerName: joinName.trim() || "Player 2",
      playerColor: joinColor,
    });
    if (result.type === "error") { setMpError(result.message); return; }
    roomCodeRef.current = joinCode.trim();
    mpPlayerIdRef.current = result.playerId;
    setMpPlayerId(result.playerId);
    setMpToken(result.token);
    setRoomCode(joinCode.trim());
    setOpponentName(result.opponentName);
    setMpConnected(true);
    setMessage(`Connected with ${result.opponentName}. Press New Game to start!`);
    startPolling();
  }

  async function handleLeaveGame() {
    await apiPost("/api/leave", { code: roomCode, playerId: mpPlayerId, token: mpToken });
    stopPolling();
    setMpConnected(false);
    setMpView("lobby");
    setRoomCode("");
    setOpponentName("");
    setMpPlayerId(null);
    setMpToken("");
    setMpScores([0, 0]);
    newGame(LEVEL_PAIRS[level]);
    setMessage("Left the game.");
  }

  function renderLobby() {
    if (mpConnected) return null;
    if (roomCode && !mpConnected) {
      return (
        <div className="mp-lobby">
          <div className="mp-code-display">
            <p className="mp-label">Share this code with a friend:</p>
            <p className="mp-code">{roomCode}</p>
            <p className="mp-waiting">Waiting for opponent to join...</p>
          </div>
          <button className="mp-cancel" onClick={handleLeaveGame}>Cancel</button>
        </div>
      );
    }
    if (mpView === "join-form") {
      return (
        <div className="mp-lobby">
          <h3 className="mp-heading">Join a Room</h3>
          <div className="mp-form">
            <label>Room Code <input value={joinCode} onChange={(e) => setJoinCode(e.target.value)} placeholder="e.g. apple-blue-rain" maxLength={40} /></label>
            <label>Your Name <input value={joinName} onChange={(e) => setJoinName(e.target.value)} maxLength={20} placeholder="Player 2" /></label>
            <label>Your Color <select value={joinColor} onChange={(e) => setJoinColor(e.target.value as PlayerColor)}>{COLOR_OPTIONS.map((c) => (<option key={c} value={c}>{toLabel(c)}</option>))}</select></label>
            <div className="mp-actions">
              <button className="mp-btn" onClick={handleJoinRoom}>Join Room</button>
              <button className="mp-btn mp-btn-secondary" onClick={() => { setMpView("lobby"); setMpError(""); }}>Back</button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="mp-lobby">
        <h3 className="mp-heading">Multiplayer</h3>
        <div className="mp-actions">
          <button className="mp-btn" onClick={handleCreateRoom}>Create Room</button>
          <button className="mp-btn mp-btn-secondary" onClick={() => setMpView("join-form")}>Join Room</button>
        </div>
        <div className="mp-color-form">
          <label>Your Name <input value={p1Name} onChange={(e) => setP1Name(e.target.value)} maxLength={20} placeholder="Player 1" /></label>
          <label>Your Color <select value={p1Color} onChange={(e) => setP1Color(e.target.value as PlayerColor)}>{COLOR_OPTIONS.map((c) => (<option key={c} value={c}>{toLabel(c)}</option>))}</select></label>
        </div>
      </div>
    );
  }

  // ---- main render --------------------------------------------------------

  return (
    <div className="game-shell">
      <ConfettiSplash
        active={game.isComplete}
        winnerName={
          game.isComplete
            ? mode === "multiplayer"
              ? mpScores[0] > mpScores[1] ? "You" : mpScores[1] > mpScores[0] ? (opponentName || "Opponent") : undefined
              : mode === "local"
                ? localScores[0] > localScores[1] ? p1 : localScores[1] > localScores[0] ? p2 : undefined
                : undefined
            : undefined
        }
      />
      {!hideTitle && <h1 className="title">Memory Match</h1>}

      <section className="control-panel">
        <div className="row two-col">
          <label>
            Mode
            <select value={mode} onChange={(e) => handleModeChange(e.target.value as Mode)}>
              <option value="single">Single Player</option>
              <option value="local">Two Players Local (Swap Turns)</option>
              <option value="multiplayer">Multiplayer Online</option>
            </select>
          </label>
          {mode !== "multiplayer" && (
            <label>
              Level
              <select value={level} onChange={(e) => { setLevel(e.target.value as Level); newGame(LEVEL_PAIRS[e.target.value as Level]); }}>
                <option value="easy">Easy (4×2)</option>
                <option value="medium">Medium (4×4)</option>
                <option value="hard">Hard (6×4)</option>
              </select>
            </label>
          )}
        </div>

        {mode === "local" && (
          <div className="row two-col">
            <label>Player 1 <input value={lP1Name} onChange={(e) => setLP1Name(e.target.value)} maxLength={20} /></label>
            <label>Player 2 <input value={lP2Name} onChange={(e) => setLP2Name(e.target.value)} maxLength={20} /></label>
          </div>
        )}

        {mode === "multiplayer" && !mpConnected && renderLobby()}

        {mode === "multiplayer" && mpConnected && (
          <div className="mp-connected-bar">
            <span className="mp-connected-info">Connected with <strong>{opponentName}</strong></span>
          </div>
        )}

        <div className="actions">
          {(mode !== "multiplayer" || mpConnected) && <button onClick={resetGame}>New Game</button>}
          {mode === "multiplayer" && mpConnected && <button className="mp-leave" onClick={handleLeaveGame}>Leave Game</button>}
        </div>

        {mpError && <p className="subtle mp-error">{mpError}</p>}
        {message && mode !== "multiplayer" && <p className="subtle">{message}</p>}
      </section>

      {(mode !== "multiplayer" || mpConnected) && (
        <section className="board-panel">
          <p className="status">{status}</p>
          <div key={gameId} className="toy-frame mm-grid" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)`, width: `min(${gridWidth}px, 100%)` }}>
            {game.cards.map((card, i) => (
              <button
                key={card.id}
                onClick={() => handleFlip(i)}
                className={`mm-card ${card.state === "revealed" ? "mm-revealed" : card.state === "matched" ? "mm-matched" : ""}`}
                disabled={
                  mode === "multiplayer"
                    ? card.state !== "hidden" || game.waiting || game.isComplete || !yourTurn || !mpConnected
                    : card.state !== "hidden" || game.waiting || game.isComplete
                }
                aria-label={`Card ${i + 1}`}
              >
                <span className="mm-face">{card.state === "hidden" ? "?" : card.emoji}</span>
              </button>
            ))}
          </div>
          <div className="how-to-play">
            <p className="how-title">How to Play</p>
            {mode === "multiplayer" ? (
              <>
                <p>1. Take turns revealing two cards per turn.</p>
                <p>2. Find a matching pair to score a point and go again.</p>
                <p>3. Most pairs at the end wins!</p>
              </>
            ) : mode === "local" ? (
              <>
                <p>1. Players take turns flipping two cards each.</p>
                <p>2. Match a pair to score a point and keep your turn.</p>
                <p>3. Most points when all pairs are found wins!</p>
              </>
            ) : (
              <>
                <p>1. Click any card to reveal the emoji.</p>
                <p>2. Find the matching pair by remembering where each emoji is.</p>
                <p>3. Match all pairs in as few moves as possible.</p>
              </>
            )}
          </div>
        </section>
      )}
    </div>
  );
}